import { useCallback, useEffect, useRef, useState } from 'react';
import type { CheckResult, DomainStatus } from '@domaintriage/shared';
import { scoreDomain } from '../lib/heuristics';

interface DrillInPanelProps {
  result: CheckResult;
  onClose: () => void;
  isShortlisted: boolean;
  onToggleShortlist: () => void;
  onRetry: () => void;
}

const NOTES_PREFIX = 'domaintriage-note-';

function StatusBadge({ status }: { status: DomainStatus }) {
  const config: Record<DomainStatus, { bg: string; text: string; label: string }> = {
    available: {
      bg: 'bg-green-100 dark:bg-green-900/40',
      text: 'text-green-800 dark:text-green-300',
      label: 'Available',
    },
    registered: {
      bg: 'bg-red-100 dark:bg-red-900/40',
      text: 'text-red-800 dark:text-red-300',
      label: 'Registered',
    },
    unknown: {
      bg: 'bg-amber-100 dark:bg-amber-900/40',
      text: 'text-amber-800 dark:text-amber-300',
      label: 'Unknown',
    },
  };
  const c = config[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${c.bg} ${c.text}`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          status === 'available'
            ? 'bg-green-500'
            : status === 'registered'
              ? 'bg-red-500'
              : 'bg-amber-500'
        }`}
        aria-hidden="true"
      />
      {c.label}
    </span>
  );
}

function confidenceLabel(status: DomainStatus, result: CheckResult): string {
  if (status === 'registered') {
    if (result.rdapEvidence?.found) return 'High -- confirmed via RDAP';
    if (result.dnsEvidence?.hasRecords) return 'Medium -- DNS records found';
    return 'Low';
  }
  if (status === 'available') {
    if (!result.dnsEvidence?.hasRecords && result.rdapEvidence && !result.rdapEvidence.found) {
      return 'High -- no DNS records and no RDAP registration';
    }
    if (!result.dnsEvidence?.hasRecords) return 'Medium -- no DNS records found';
    return 'Low';
  }
  // unknown
  if (result.dnsEvidence?.error || result.rdapEvidence?.error) {
    return 'Low -- check errors occurred';
  }
  return 'Low -- inconclusive data';
}

function formatDateTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
}

export function DrillInPanel({
  result,
  onClose,
  isShortlisted,
  onToggleShortlist,
  onRetry,
}: DrillInPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState(false);
  const [notes, setNotes] = useState(() => {
    try {
      return localStorage.getItem(`${NOTES_PREFIX}${result.domain}`) ?? '';
    } catch {
      return '';
    }
  });

  // Focus trap and Escape key
  useEffect(() => {
    closeButtonRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Persist notes
  const handleNotesChange = useCallback(
    (value: string) => {
      setNotes(value);
      try {
        if (value) {
          localStorage.setItem(`${NOTES_PREFIX}${result.domain}`, value);
        } else {
          localStorage.removeItem(`${NOTES_PREFIX}${result.domain}`);
        }
      } catch {
        // ignore
      }
    },
    [result.domain],
  );

  async function handleCopyDomain() {
    try {
      await navigator.clipboard.writeText(result.domain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  }

  const scores = scoreDomain(result.domain);

  const lengthScoreLabel =
    scores.lengthScore === 'good'
      ? 'Good length'
      : scores.lengthScore === 'okay'
        ? 'Acceptable length'
        : 'Suboptimal length';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Details for ${result.domain}`}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl transition-transform sm:rounded-l-xl dark:bg-gray-900"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white/95 px-6 py-4 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/95">
          <h2 className="truncate font-mono text-lg font-bold text-gray-900 dark:text-white">
            {result.domain}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-6 p-6">
          {/* A) Summary */}
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Summary
            </h3>
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                <StatusBadge status={result.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Confidence</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {confidenceLabel(result.status, result)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Checked at</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {formatDateTime(result.checkedAt)}
                </span>
              </div>
              {result.cached && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Source</span>
                  <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    Cached
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* B) Evidence */}
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Evidence
            </h3>
            <div className="space-y-4">
              {/* DNS */}
              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                  DNS Records
                </h4>
                {result.dnsEvidence ? (
                  result.dnsEvidence.error ? (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      Error: {result.dnsEvidence.error}
                    </p>
                  ) : result.dnsEvidence.hasRecords ? (
                    <ul className="space-y-1.5">
                      {result.dnsEvidence.records.map((rec) => (
                        <li
                          key={`${rec.type}-${rec.values.join(',')}`}
                          className="text-sm"
                        >
                          <span className="inline-block w-14 font-mono font-semibold text-gray-600 dark:text-gray-400">
                            {rec.type}
                          </span>
                          <span className="font-mono text-gray-800 dark:text-gray-200">
                            {rec.values.join(', ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No DNS records found.
                    </p>
                  )
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    DNS check not performed.
                  </p>
                )}
                {result.dnsEvidence && (
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    Checked: {formatDateTime(result.dnsEvidence.checkedAt)}
                  </p>
                )}
              </div>

              {/* RDAP */}
              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                  RDAP Registration
                </h4>
                {result.rdapEvidence ? (
                  result.rdapEvidence.error ? (
                    <div>
                      <p className="text-sm text-red-600 dark:text-red-400">
                        Error: {result.rdapEvidence.error}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        The RDAP server may be unavailable, rate-limited, or the TLD may not support
                        RDAP queries.
                      </p>
                    </div>
                  ) : result.rdapEvidence.found ? (
                    <dl className="space-y-2">
                      {result.rdapEvidence.registrar && (
                        <div>
                          <dt className="text-xs text-gray-500 dark:text-gray-400">Registrar</dt>
                          <dd className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {result.rdapEvidence.registrar}
                          </dd>
                        </div>
                      )}
                      {result.rdapEvidence.creationDate && (
                        <div>
                          <dt className="text-xs text-gray-500 dark:text-gray-400">Created</dt>
                          <dd className="text-sm text-gray-800 dark:text-gray-200">
                            {formatDateTime(result.rdapEvidence.creationDate)}
                          </dd>
                        </div>
                      )}
                      {result.rdapEvidence.expirationDate && (
                        <div>
                          <dt className="text-xs text-gray-500 dark:text-gray-400">Expires</dt>
                          <dd className="text-sm text-gray-800 dark:text-gray-200">
                            {formatDateTime(result.rdapEvidence.expirationDate)}
                          </dd>
                        </div>
                      )}
                      {result.rdapEvidence.statuses && result.rdapEvidence.statuses.length > 0 && (
                        <div>
                          <dt className="text-xs text-gray-500 dark:text-gray-400">Statuses</dt>
                          <dd className="flex flex-wrap gap-1.5 pt-0.5">
                            {result.rdapEvidence.statuses.map((s) => (
                              <span
                                key={s}
                                className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                              >
                                {s}
                              </span>
                            ))}
                          </dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No RDAP registration record found.
                    </p>
                  )
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    RDAP check not performed.
                  </p>
                )}
                {result.rdapEvidence && (
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    Checked: {formatDateTime(result.rdapEvidence.checkedAt)}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* C) Heuristics */}
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Domain Quality Heuristics
            </h3>
            <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              {/* Length */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Length</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {scores.length} chars
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      scores.lengthScore === 'good'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : scores.lengthScore === 'okay'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    }`}
                  >
                    {lengthScoreLabel}
                  </span>
                </span>
              </div>

              {/* Hyphens */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Hyphens</span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    scores.hasHyphens
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  }`}
                >
                  {scores.hasHyphens ? 'Contains hyphens' : 'No hyphens'}
                </span>
              </div>

              {/* Numbers */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Numbers</span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    scores.hasNumbers
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  }`}
                >
                  {scores.hasNumbers ? 'Contains numbers' : 'No numbers'}
                </span>
              </div>
            </div>
          </section>

          {/* D) Actions */}
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Actions
            </h3>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleCopyDomain}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {copied ? (
                    <>
                      <svg className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M7 3.5A1.5 1.5 0 018.5 2h3.879a1.5 1.5 0 011.06.44l3.122 3.12A1.5 1.5 0 0117 6.622V12.5a1.5 1.5 0 01-1.5 1.5h-1v-3.379a3 3 0 00-.879-2.121L10.5 5.379A3 3 0 008.379 4.5H7v-1z" />
                        <path d="M4.5 6A1.5 1.5 0 003 7.5v9A1.5 1.5 0 004.5 18h7a1.5 1.5 0 001.5-1.5v-5.879a1.5 1.5 0 00-.44-1.06L9.44 6.439A1.5 1.5 0 008.378 6H4.5z" />
                      </svg>
                      Copy Domain
                    </>
                  )}
                </button>

                <a
                  href={`https://www.namecheap.com/domains/registration/results/?domain=${encodeURIComponent(result.domain)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                    <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                  </svg>
                  Namecheap
                </a>

                <a
                  href={`https://www.google.com/search?q=${encodeURIComponent(result.domain)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                  </svg>
                  Google Search
                </a>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onToggleShortlist}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    isShortlisted
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50'
                      : 'border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill={isShortlisted ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth={2}
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  {isShortlisted ? 'Remove from Shortlist' : 'Add to Shortlist'}
                </button>

                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 01-9.379 2.624l-1.06 1.06a7 7 0 0011.929-3.342.75.75 0 00-1.49-.342zm-10.624-2.85a5.5 5.5 0 019.375-2.572l1.06-1.06A7 7 0 003.193 8.284a.75.75 0 001.494.29z" clipRule="evenodd" />
                  </svg>
                  Retry
                </button>
              </div>

              {/* Notes */}
              <div>
                <label
                  htmlFor="domain-notes"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Notes
                </label>
                <textarea
                  id="domain-notes"
                  value={notes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Add your notes about this domain..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-400"
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Notes are saved in your browser only.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
